import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { BrandSummary, Tag } from "@shared/types";
import { api } from "../lib/api";
import { BrandGrid } from "../components/BrandGrid";
import { FilterPanel, type FilterState } from "../components/FilterPanel";
import { EmptyState } from "../components/EmptyState";
import { isAbortError } from "../lib/asyncState";

export function BrandExplorerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<BrandSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<FilterState>(() => filtersFromSearch(searchParams));
  const [categories, setCategories] = useState<Tag[]>([]);
  const [styles, setStyles] = useState<Tag[]>([]);
  const [radarStatus, setRadarStatus] = useState<Awaited<ReturnType<typeof api.radar.status>> | null>(null);
  const [radarTrends, setRadarTrends] = useState<Awaited<ReturnType<typeof api.radar.trends>>["items"]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.categories.list({ occupied: true }).then((r) => setCategories(r.categories.filter((c) => c.type === "primary_category")));
    api.tags.list("style_direction", { occupied: true }).then((r) => setStyles(r.tags));
    api.radar.status().then(setRadarStatus).catch(() => setRadarStatus(null));
    api.radar.trends(8).then((r) => setRadarTrends(r.items)).catch(() => setRadarTrends([]));
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api.brands.list({
      category: filters.category,
      tag: filters.styleDirection,
      priceLevel: filters.priceLevel || undefined,
      fresh: filters.fresh,
      sort: filters.sort,
      limit: 48,
    }, { signal: controller.signal }).then((r) => {
      setItems(r.items);
      setTotal(r.total);
    }).catch((err) => {
      if (isAbortError(err)) return;
      setItems([]);
      setTotal(0);
      setError("Could not load brands right now.");
    }).finally(() => {
      if (!controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [filters]);

  const updateFilters = (next: FilterState) => {
    setFilters(next);
    setSearchParams(searchFromFilters(next), { replace: true });
  };

  return (
    <div className="container" style={{ paddingBlock: "2rem" }}>
      <div className="section__head">
        <h2>{filters.fresh === "week" ? "New this week" : "Brand explorer"}</h2>
        <div style={{ color: "var(--fg-subtle)", fontSize: "0.9rem" }}>
          {total} {filters.fresh === "week" ? "fresh" : ""} brands
        </div>
      </div>
      {radarStatus && (
        <section className="radar-status" aria-label="Live radar status">
          <div>
            <span>Live radar</span>
            <b>{radarStatus.freshBrands7d}</b>
            <small>new this week</small>
          </div>
          <div>
            <span>Published</span>
            <b>{radarStatus.publishedBrands}</b>
            <small>brands tracked</small>
          </div>
          <div>
            <span>Crowd</span>
            <b>{radarStatus.crowdVotes}</b>
            <small>{radarStatus.crowdVotes === 1 ? "vote cast" : "votes cast"}</small>
          </div>
          <div>
            <span>Latest find</span>
            <b>{radarStatus.latestBrand?.name ?? "Scanning"}</b>
            <small>{formatRadarTime(radarStatus.lastRadarActivityAt)}</small>
          </div>
        </section>
      )}
      {radarTrends.length > 0 && (
        <section className="radar-trends" aria-label="Signals moving this week">
          <div className="radar-trends__head">
            <span>Signal heat</span>
            <b>What is moving this week</b>
          </div>
          <div className="radar-trends__rail">
            {radarTrends.map((trend) => (
              <Link
                key={`${trend.type}:${trend.slug}`}
                to={trend.type === "primary_category" ? `/categories/${trend.slug}` : `/tags/${trend.slug}`}
                className={trend.type === "primary_category" ? "radar-trend is-category" : "radar-trend"}
              >
                <span>{trend.name}</span>
                <b>{trend.brandCount}</b>
                <small>{trend.avgMomentum >= 70 ? "high momentum" : `disc ${trend.avgDiscovery}`}</small>
              </Link>
            ))}
          </div>
        </section>
      )}
      <FilterPanel categories={categories} styles={styles} value={filters} onChange={updateFilters} />
      {loading ? (
        <div className="empty-state">Loading…</div>
      ) : error ? (
        <EmptyState title="Brands could not load" actions={<button className="primary" onClick={() => setFilters({ ...filters })}>Try again</button>}>
          {error}
        </EmptyState>
      ) : (
        <BrandGrid brands={items} />
      )}
    </div>
  );
}

function formatRadarTime(value: string | null): string {
  if (!value) return "waiting for signal";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "recently active";
  return `active ${date.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
}

function filtersFromSearch(params: URLSearchParams): FilterState {
  return {
    category: params.get("category") || undefined,
    styleDirection: params.get("tag") || undefined,
    priceLevel: (params.get("priceLevel") || undefined) as FilterState["priceLevel"],
    fresh: params.get("fresh") === "week" ? "week" : undefined,
    sort: params.get("sort") || "crowd",
  };
}

function searchFromFilters(filters: FilterState): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.category) params.set("category", filters.category);
  if (filters.styleDirection) params.set("tag", filters.styleDirection);
  if (filters.priceLevel) params.set("priceLevel", filters.priceLevel);
  if (filters.fresh) params.set("fresh", filters.fresh);
  if (filters.sort && filters.sort !== "crowd") params.set("sort", filters.sort);
  return params;
}
